import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [range, setRange] = useState("8");

  useEffect(() => {
    axios.get(`${API}/public/client-portal/${token}?weeks=${range}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [token, range]);

  if (error) {
    return (
      <div className="min-h-screen bg-app text-white flex items-center justify-center p-6">
        <div className="text-center" data-testid="portal-invalid">
          <div className="font-display text-2xl font-bold">This link is no longer active</div>
          <p className="text-sm text-slate-500 mt-2">Ask your agency for a fresh performance link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-app flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const b = data.branding || {};
  const e = data.entity || {};
  const adv = data.advertising || {};
  const fr = data.fundraising || {};
  const accent = b.accent_color || "var(--accent-primary)";

  return (
    <div className="min-h-screen bg-app text-white" data-testid="client-portal">
      {/* Agency-branded header */}
      <header className="border-b border-white/10 bg-sidebar">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
          {b.agency_logo_url && (
            <img src={b.agency_logo_url} alt={b.agency_name || "Agency"} data-testid="portal-agency-logo"
              className="h-10 max-w-[180px] object-contain rounded" onError={(ev) => { ev.target.style.display = "none"; }} />
          )}
          <div>
            {b.agency_name && <div className="font-display text-lg font-bold" data-testid="portal-agency-name">{b.agency_name}</div>}
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Client performance report</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={() => window.print()} data-testid="portal-pdf-btn"
              className="portal-no-print text-[11px] border border-white/20 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 rounded-full px-3.5 py-1.5 transition-colors">
              Download PDF
            </button>
            <div className="text-[10px] text-slate-600">Powered by TotalReach</div>
          </div>
        </div>
      </header>
      <style>{`@media print {
        body { background: #fff !important; }
        .portal-no-print, [data-testid="portal-range-toggle"] { display: none !important; }
        [data-testid="client-portal"] { background: #fff !important; color: #111 !important; }
        [data-testid="client-portal"] * { color: #111 !important; border-color: #ddd !important; background: transparent !important; }
      }`}</style>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold" data-testid="portal-entity-name">{e.name}</h1>
            <div className="text-sm text-slate-400 mt-1.5">
              {[e.office, e.state && `${e.state}${e.district ? `-${e.district}` : ""}`].filter(Boolean).join(" · ")}
            </div>
          </div>
          {e.days_until != null && e.days_until >= 0 && (
            <div className="text-center px-8 py-4 rounded-2xl border bg-white/[0.03]" style={{ borderColor: `color-mix(in srgb, ${accent} 27%, transparent)` }} data-testid="portal-countdown">
              <div className="font-display text-5xl font-bold" style={{ color: accent }}>{e.days_until}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Days until Election Day</div>
            </div>
          )}
        </div>

        {data.evidence?.note && (
          <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[11px] text-slate-400" data-testid="portal-evidence-note">
            {data.evidence.note}
          </div>
        )}

        {data.testimonial?.quote && (
          <figure className="mb-10 rounded-2xl border bg-white/[0.03] px-6 py-5" style={{ borderColor: `color-mix(in srgb, ${accent} 20%, transparent)` }} data-testid="portal-testimonial">
            <div className="font-display text-4xl leading-none mb-1" style={{ color: accent }}>“</div>
            <blockquote className="text-lg sm:text-xl text-slate-100 font-medium leading-relaxed" data-testid="portal-testimonial-text">
              {data.testimonial.quote}
            </blockquote>
            {data.testimonial.author && (
              <figcaption className="mt-3 text-xs uppercase tracking-widest text-slate-400" data-testid="portal-testimonial-attribution">
                — {data.testimonial.author}
              </figcaption>
            )}
          </figure>
        )}

        {data.memo?.memo && (
          <details className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="portal-memo">
            <summary className="px-6 py-4 cursor-pointer text-sm font-semibold flex items-center gap-2" style={{ color: accent }}>
              Weekly strategy memo · {data.memo.week}
            </summary>
            <div className="px-6 pb-5 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="portal-memo-body">
              {data.memo.memo}
            </div>
          </details>
        )}

        {data.spend?.series?.length > 0 && (
          <div className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="portal-spend">
            <div className="px-6 pt-5">
              <div className="text-xs uppercase tracking-widest text-slate-400">What we're up against</div>
              <div className="text-lg font-semibold mt-0.5" style={{ color: accent }}>Reported ad spend in this race</div>
            </div>
            <div className="h-52 px-3 py-4" data-testid="portal-spend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.spend.series} margin={{ top: 6, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(n) => "$" + new Intl.NumberFormat("en-US", { notation: "compact" }).format(n)}
                    tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
                  <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.12)", borderRadius: 8, fontSize: 11 }}
                    formatter={(v) => "$" + new Intl.NumberFormat("en-US", { notation: "compact" }).format(v)} labelStyle={{ color: "var(--text-muted)" }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {data.spend.candidates.map((c, i) => (
                    <Line key={c} type="monotone" dataKey={c}
                      stroke={["var(--accent-primary)", "var(--pink)", "var(--accent-primary)", "var(--accent-primary)", "var(--plum)", "var(--danger)", "var(--info)", "var(--warning)"][i % 8]}
                      strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="px-6 pb-4 text-[10px] text-slate-500">Figures as reported by ad trackers, FEC filings and press coverage.</div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10" data-testid="portal-stats">
          <Stat label="Advertising budget" value={usd(adv.total_budget)} tag={data.evidence?.advertising} />
          <Stat label="Active campaigns" value={`${adv.active_campaigns || 0} of ${adv.total_campaigns || 0}`} tag={data.evidence?.advertising} />
          <Stat label="Funds raised" value={usd(fr.total_raised)} tag={data.evidence?.fundraising} />
          <Stat label="Donors" value={String(fr.donors || 0)} tag={data.evidence?.fundraising} />
        </div>

        {(data.timeseries || []).length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-10" data-testid="portal-chart">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
              <span className="font-display text-lg font-bold">
                Momentum — {range === "all" ? "full campaign" : `last ${range} weeks`}
              </span>
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 p-0.5" data-testid="portal-range-toggle">
                {[["8", "8 wk"], ["12", "12 wk"], ["all", "Full campaign"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRange(v)} data-testid={`portal-range-${v}`}
                    className="text-[11px] px-3 py-1 rounded-full font-semibold transition-colors"
                    style={range === v ? { background: accent, color: "var(--ink)" } : { color: "var(--text-muted)" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-3 py-4" style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.timeseries} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <XAxis dataKey="week" tick={{ fill: "var(--text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                  <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "var(--text-primary)" }} formatter={(v, n) => [usd(v), n === "donations" ? "Donations received" : "New ad budget"]} />
                  <Legend formatter={(v) => <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{v === "donations" ? "Donations received" : "New ad budget"}</span>} />
                  <Bar dataKey="donations" fill={accent} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ad_budget" fill="var(--chart-5)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-10" data-testid="portal-campaigns">
          <div className="px-5 py-4 border-b border-white/5 font-display text-lg font-bold">Campaigns</div>
          {(data.campaigns || []).length === 0 ? (
            <p className="text-sm text-slate-500 px-5 py-6">No campaigns yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/5">
                  <th className="px-5 py-2.5">Campaign</th>
                  <th className="px-5 py-2.5">Channels</th>
                  <th className="px-5 py-2.5">Status</th>
                  <th className="px-5 py-2.5 text-right">Budget</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`portal-campaign-${i}`}>
                    <td className="px-5 py-3 text-white">{c.name}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{(c.channels || []).join(", ")}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${c.status === "Active" ? "text-emerald-300 border-emerald-400/30" : "text-slate-400 border-white/15"}`}>{c.status}</span>
                    </td>
                    <td className="px-5 py-3 text-right">{usd(c.total_budget)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {(data.fundraisers || []).length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-10" data-testid="portal-fundraising">
            <div className="px-5 py-4 border-b border-white/5 font-display text-lg font-bold">Fundraising momentum</div>
            <div className="divide-y divide-white/5">
              {data.fundraisers.map((f, i) => {
                const trendUp = f.last7 > f.prev7;
                const trendFlat = f.last7 === f.prev7;
                return (
                  <div key={i} className="px-5 py-4" data-testid={`portal-fundraiser-${i}`}>
                    <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                      <span className="text-sm text-white font-semibold">{f.name}</span>
                      <span className="text-sm text-slate-300">{usd(f.raised)} <span className="text-slate-500 text-xs">of {usd(f.goal)} goal · {f.donors} donors</span></span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.min(100, f.pct)}%`, background: accent }} />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-slate-500">{f.pct}% of goal</span>
                      <span className={`text-[11px] ${trendFlat ? "text-slate-500" : trendUp ? "text-emerald-300" : "text-amber-300"}`}>
                        {usd(f.last7)} last 7 days {trendFlat ? "· steady" : trendUp ? `· up from ${usd(f.prev7)}` : `· down from ${usd(f.prev7)}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center text-[11px] text-slate-600 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Read-only report prepared {b.agency_name ? `by ${b.agency_name} ` : ""}via TotalReach — figures update live.
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value, tag }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
    <div className="font-display text-xl font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    {tag && <div className="text-[9px] text-slate-600 mt-1.5 inline-block border border-white/10 rounded-full px-2 py-0.5" data-testid="stat-evidence-tag">{tag}</div>}
  </div>
);
